const MIN_PROS_TO_CHOOSE_FROM = 2;
const MIN_SCORES_FOR_AVERAGE = 3;
const MIN_PHOTOS_TO_CHOOSE_FROM = 2;
const MAX_SUB_TASKS = 3;

const VOWELS = "aáeéiíoóöőuúüű";

export function lowerFirst(text) {
  if (!text) {
    return "";
  }
  const firstWord = text.split(" ")[0];
  if (firstWord.length > 1 && firstWord === firstWord.toUpperCase()) {
    return text;
  }
  return text.charAt(0).toLowerCase() + text.slice(1);
}

export function capitalizeFirst(text) {
  if (!text) {
    return "";
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function withArticle(word) {
  if (!word) {
    return "";
  }
  const article = VOWELS.includes(word.charAt(0).toLowerCase()) ? "az" : "a";
  return `${article} ${word}`;
}

export function joinNames(names) {
  const filtered = names.filter(Boolean);
  if (filtered.length <= 1) {
    return filtered[0] ?? "";
  }
  return `${filtered.slice(0, -1).join(", ")} és ${filtered.at(-1)}`;
}

export function cityForms(city) {
  const name = city?.name ?? "";
  return {
    name,
    inCity: city?.nameInessive || `${name} területén`,
    inCityAndSurroundings: `${city?.nameInessive || name} és környékén`,
    adjective: city?.nameAdjective || `${name} környéki`,
  };
}

export const HUB_STAT_PLACEHOLDERS = [
  "professionalCount",
  "cityCount",
  "scoreCount",
  "averageScore",
  "completedJobCount",
];

const SENTENCE_SELECTOR = "p, li";

export function hubStatValues(stats, { formatNumber, formatRating }) {
  const count = (value) => (value > 0 ? formatNumber(value) : null);
  const hasAverage =
    stats?.averageScore > 0 && stats.scoreCount >= MIN_SCORES_FOR_AVERAGE;
  return {
    professionalCount: count(stats?.professionalCount),
    cityCount: count(stats?.cityCount),
    scoreCount: count(stats?.scoreCount),
    averageScore: hasAverage ? formatRating(stats.averageScore / 2) : null,
    completedJobCount: count(stats?.completedJobCount),
  };
}

function placeholderOf(name) {
  return `{${name}}`;
}

function replaceStatPlaceholders(html, values, domParser) {
  const usedNames = HUB_STAT_PLACEHOLDERS.filter((name) =>
    html.includes(placeholderOf(name))
  );
  if (!usedNames.length) {
    return html;
  }
  const missingNames = usedNames.filter((name) => values[name] == null);
  let result = html;
  if (missingNames.length && domParser) {
    const { body } = domParser.parseFromString(html, "text/html");
    body.querySelectorAll(SENTENCE_SELECTOR).forEach((element) => {
      if (
        missingNames.some((name) =>
          element.textContent.includes(placeholderOf(name))
        )
      ) {
        element.remove();
      }
    });
    result = body.innerHTML;
  }
  return usedNames.reduce(
    (text, name) => text.replaceAll(placeholderOf(name), values[name] ?? ""),
    result
  );
}

export function replaceTaskPagePlaceholders(
  text,
  { taskName, cityName, professionName, statValues = null, domParser = null }
) {
  if (!text) {
    return text;
  }
  const replaced = text
    .replaceAll("{taskName}", taskName ?? "")
    .replaceAll("{cityName}", cityName ?? "")
    .replaceAll("{professionName}", professionName ?? "");
  return (
    statValues ? replaceStatPlaceholders(replaced, statValues, domParser) : replaced
  )
    .replace(/ {2,}/g, " ")
    .trim();
}

function formatPriceRange(range, formatCurrency) {
  const value = `${formatCurrency(range.priceFrom)} – ${formatCurrency(range.priceTo)}`;
  return range.unit ? `${value} / ${range.unit}` : value;
}

function section(id, heading, items) {
  const filtered = items.filter(Boolean);
  if (!filtered.some((item) => item.type === "text")) {
    return null;
  }
  return { id, heading, items: filtered };
}

function text(value) {
  return value ? { type: "text", value } : null;
}

export function buildProfessionTaskCityContent({
  t,
  formatCurrency,
  formatRating,
  page,
  city,
  stats,
  professionalCount,
  siblingTasks = [],
}) {
  const forms = cityForms(city);
  const task = lowerFirst(page.professionTask.name);
  const subTasks = joinNames(
    siblingTasks
      .filter((sibling) => String(sibling.id) !== String(page.professionTask.id))
      .slice(0, MAX_SUB_TASKS)
      .map((sibling) => lowerFirst(sibling.name))
  );
  const proCount = professionalCount ?? stats?.professionalCount ?? 0;
  const params = {
    task,
    theTask: withArticle(task),
    subTasks,
    cityName: forms.name,
    theCityName: withArticle(forms.name),
    inCity: forms.inCity,
    TheInCity: capitalizeFirst(withArticle(forms.inCity)),
    inCityAndSurroundings: forms.inCityAndSurroundings,
    cityAdjective: forms.adjective,
    theCityAdjective: withArticle(forms.adjective),
  };
  const tr = (key, extra = {}) => t(`professionTaskPage.city.${key}`, { ...params, ...extra });

  const lead = [
    tr(subTasks ? "leadFind" : "leadFindWithoutSubTasks"),
    tr("leadAbout"),
  ].join(" ");

  const priceRanges = (stats?.priceRanges ?? []).filter((range) => range.sampleSize >= 3);
  const recentTenders = stats?.recentTenders ?? [];
  const nearbyCities = stats?.nearbyCities ?? [];
  const averageStars = stats?.averageScore != null ? stats.averageScore / 2 : null;

  const sections = [
    section("munkak", tr("worksHeading"), [
      subTasks && text(tr("worksTypical")),
      proCount >= MIN_PROS_TO_CHOOSE_FROM &&
        text(tr(subTasks ? "worksChoose" : "worksChooseWithoutSubTasks", { count: proCount })),
    ]),
    section("arak", tr("pricesHeading"), [
      priceRanges.length &&
        text(
          tr("pricesBooking", {
            services: joinNames(priceRanges.map((range) => lowerFirst(range.professionTask.name))),
          })
        ),
      priceRanges.length &&
        text(
          tr("pricesRanges", {
            ranges: priceRanges
              .map(
                (range) =>
                  `${lowerFirst(range.professionTask.name)}: ${formatPriceRange(range, formatCurrency)}`
              )
              .join("; "),
          })
        ),
    ]),
    section("ajanlatkeresek", tr("tendersHeading"), [
      recentTenders.length && text(tr("tendersIntro")),
      recentTenders.length && {
        type: "quotes",
        value: recentTenders.map((tender) => tender.text),
      },
    ]),
    section("szakemberek-szama", tr("countHeading"), [
      proCount >= 1 && text(tr("countPros", { count: proCount })),
      stats?.tenderCount > 0 &&
        text(
          tr("countTenders", {
            count: stats.tenderCount,
            professions: joinNames(page.professions.map((profession) => lowerFirst(profession.name))),
          })
        ),
      stats?.completedJobCount > 0 && text(tr("countJobs", { count: stats.completedJobCount })),
      averageStars != null &&
        stats.scoreCount >= MIN_SCORES_FOR_AVERAGE &&
        text(
          tr("countScores", {
            stars: formatRating(averageStars),
            count: stats.scoreCount,
          })
        ),
    ]),
    section("valasztas", tr("chooseHeading"), [
      text(tr("chooseStatic")),
      stats?.referencePhotoCount >= MIN_PHOTOS_TO_CHOOSE_FROM &&
        text(tr("choosePhotos", { count: stats.referencePhotoCount })),
    ]),
    section("kornyek", tr("compareHeading"), [
      nearbyCities.length &&
        text(tr("compareNearby", { cities: joinNames(nearbyCities.map((nearby) => nearby.name)) })),
      nearbyCities.length && { type: "cityLinks", value: nearbyCities },
    ]),
    section("miert", tr("whyHeading"), [text(tr("whyPlatform")), text(tr("whyQualified"))]),
  ].filter(Boolean);

  return {
    heading: t("professionTaskPage.city.heading", {
      ...params,
      task: capitalizeFirst(task),
    }),
    lead,
    sections,
  };
}
